import { useCallback, useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useCalibration } from '../../contexts/CalibrationContext';
import {
  computeCoachProfilePct,
  type CoachLevel,
  type CommsStyle,
  type CoachProfile,
} from '@4cm/core/profiles';

/**
 * Coach profile fields beyond the calibration level. Mirrors the wizard
 * web1 ships in CoachProfileWizard.tsx — same six core fields stored on
 * `users` (migration 00025), plus the auto-computed completeness pct.
 *
 * The level itself lives in CalibrationContext (already persisted there).
 * This hook handles the wizard fields; the two together cover everything
 * the @4cm/core CoachProfile shape exposes.
 */
export interface CoachProfileExtras {
  years_coaching: number | null;
  age_groups_coached: string[];
  coach_badges: string[];
  coach_methodology: string | null;
  coach_comms_style: CommsStyle | null;
}

const DEFAULTS: CoachProfileExtras = {
  years_coaching: null,
  age_groups_coached: [],
  coach_badges: [],
  coach_methodology: null,
  coach_comms_style: null,
};

const STORAGE_KEY = '4cm_coach_profile';

export interface UseCoachProfileResult {
  /** Calibration shim — preserved so old callers (none today, but the
   *  hook signature was previously `{ coachLevel, calibrationLevel,
   *  isAcademy }`) continue to work. */
  coachLevel: CoachLevel | null;
  calibrationLevel: 'novice' | 'academy';
  isAcademy: boolean;
  /** The wizard fields. */
  extras: CoachProfileExtras;
  /** Completeness percentage 0–100 across the 6 fields (level + 5 extras). */
  pct: number;
  loading: boolean;
  save: (patch: Partial<CoachProfileExtras>) => Promise<void>;
}

function readCached(): CoachProfileExtras | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CoachProfileExtras>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return null;
  }
}

function writeCached(extras: CoachProfileExtras): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(extras));
  } catch {
    /* ignore quota / privacy mode */
  }
}

export function useCoachProfile(): UseCoachProfileResult {
  const { coachLevel, level } = useCalibration();
  const { user } = useAuth();
  const [extras, setExtras] = useState<CoachProfileExtras>(() => readCached() ?? DEFAULTS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (isPlaceholderSupabase()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from('users')
        .select('years_coaching, age_groups_coached, coach_badges, coach_methodology, coach_comms_style')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      setLoading(false);
      if (!data) return;
      const next: CoachProfileExtras = {
        years_coaching: typeof data.years_coaching === 'number' ? data.years_coaching : null,
        age_groups_coached: Array.isArray(data.age_groups_coached) ? data.age_groups_coached : [],
        coach_badges: Array.isArray(data.coach_badges) ? data.coach_badges : [],
        coach_methodology: data.coach_methodology ?? null,
        coach_comms_style: (data.coach_comms_style as CommsStyle | null) ?? null,
      };
      setExtras(next);
      writeCached(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const save = useCallback(async (patch: Partial<CoachProfileExtras>) => {
    setExtras((prev) => {
      const next: CoachProfileExtras = { ...prev, ...patch };
      writeCached(next);
      if (user && !isPlaceholderSupabase()) {
        const fullProfile: Partial<CoachProfile> = {
          coach_level: coachLevel,
          ...next,
        };
        const pct = computeCoachProfilePct(fullProfile);
        void supabase
          .from('users')
          .update({ ...patch, coach_profile_pct: pct })
          .eq('id', user.id);
      }
      return next;
    });
  }, [user, coachLevel]);

  const pct = computeCoachProfilePct({
    coach_level: coachLevel,
    ...extras,
  });

  return {
    coachLevel,
    calibrationLevel: level,
    isAcademy: level === 'academy',
    extras,
    pct,
    loading,
    save,
  };
}
